import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Shield, Check, X } from "lucide-react";

const SignUpStep3 = ({ formData, handleChange, visibleGroups = [0, 1, 2, 3], validationErrors = {} }) => {
    const [professionDropdownOpen, setProfessionDropdownOpen] = useState(false);
    const professionDropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (professionDropdownRef.current && !professionDropdownRef.current.contains(event.target)) {
                setProfessionDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle profession multi-select
    const handleProfessionChange = (value) => {
        const currentProfessions = formData.profession || [];
        let newProfessions;

        if (currentProfessions.includes(value)) {
            // Remove if already selected
            newProfessions = currentProfessions.filter(p => p !== value);
        } else {
            // Add if not at max limit (3)
            if (currentProfessions.length < 3) {
                newProfessions = [...currentProfessions, value];
            } else {
                return; // Don't add if already at max
            }
        }

        // Create synthetic event for handleChange
        handleChange({
            target: {
                name: "profession",
                value: newProfessions
            }
        });
    };

    const removeProfession = (value, e) => {
        e.stopPropagation();
        const newProfessions = (formData.profession || []).filter(p => p !== value);
        handleChange({
            target: {
                name: "profession",
                value: newProfessions
            }
        });
    };

    const professionOptions = [
        { value: "Student/Exam Aspirant", label: "Student/Exam Aspirant" },
        { value: "Working Professional", label: "Working Professional" },
        { value: "Founder", label: "Founder" },
        { value: "Researcher", label: "Researcher" },
        { value: "Service Center Operator", label: "Service Center Operator" },
        { value: "Other", label: "Other" }
    ];

    // Simplified grouped data
    const fieldGroups = [
        {
            title: "Personal Identity",
            fields: [
                {
                    name: "mother_tongue",
                    label: "Mother Tongue",
                    type: "text",
                    placeholder: "e.g., Hindi, English, Telugu"
                },
                {
                    name: "changed_name",
                    label: "Name Change Status",
                    type: "select",
                    options: [
                        { value: "", label: "Select status" },
                        { value: "No", label: "No change" },
                        { value: "Yes", label: "Changed legally" }
                    ]
                },
                {
                    name: "changedNameDetail",
                    label: "Name Change Details",
                    type: "text",
                    placeholder: "Enter previous name details (if name was changed)",
                    condition: () => formData.changed_name === "Yes"
                },
                {
                    name: "gender",
                    label: "Gender Identity",
                    type: "select",
                    options: [
                        { value: "", label: "Select identity" },
                        { value: "Male", label: "Male" },
                        { value: "Female", label: "Female" },
                        { value: "Transgender", label: "Transgender" },
                        { value: "Non-binary", label: "Non-binary" },
                        { value: "Prefer not to say", label: "Prefer not to say" }
                    ]
                }
            ]
        },
        {
            title: "Education & Career",
            fields: [
                {
                    name: "education",
                    label: "Highest Qualification",
                    type: "select",
                    options: [
                        { value: "", label: "Select qualification" },
                        { value: "High School (10th)", label: "High School (10th)" },
                        { value: "Intermediate (12th)", label: "Intermediate (12th)" },
                        { value: "Diploma", label: "Diploma" },
                        { value: "Graduate", label: "Graduate" },
                        { value: "Post Graduate", label: "Post Graduate" },
                        { value: "PhD", label: "PhD / Doctorate" }
                    ]
                },
                {
                    name: "profession",
                    label: "Profession",
                    type: "multiselect",
                    hint: "Select 1-3 options (required)",
                    options: professionOptions
                }
            ]
        },
        {
            title: "Social Identity",
            fields: [
                {
                    name: "category",
                    label: "Social Category",
                    type: "select",
                    options: [
                        { value: "", label: "Select category" },
                        { value: "General", label: "General" },
                        { value: "OBC", label: "Other Backward Class (OBC)" },
                        { value: "SC", label: "Scheduled Caste (SC)" },
                        { value: "ST", label: "Scheduled Tribe (ST)" },
                        { value: "EWS", label: "Economically Weaker Section (EWS)" }
                    ]
                },
                {
                    name: "is_disabled",
                    label: "Disability Status",
                    type: "select",
                    options: [
                        { value: "", label: "Select status" },
                        { value: "No", label: "Not disabled" },
                        { value: "Yes", label: "Person with disability" }
                    ]
                },
                {
                    name: "marital_status",
                    label: "Marital Status",
                    type: "select",
                    options: [
                        { value: "", label: "Select status" },
                        { value: "Single", label: "Single" },
                        { value: "Married", label: "Married" },
                        { value: "Divorced", label: "Divorced" },
                        { value: "Widowed", label: "Widowed" },
                        { value: "Separated", label: "Separated" }
                    ]
                },
                {
                    name: "religion",
                    label: "Religion",
                    type: "select",
                    options: [
                        { value: "", label: "Select religion" },
                        { value: "Hinduism", label: "Hinduism" },
                        { value: "Islam", label: "Islam" },
                        { value: "Christianity", label: "Christianity" },
                        { value: "Sikhism", label: "Sikhism" },
                        { value: "Buddhism", label: "Buddhism" },
                        { value: "Jainism", label: "Jainism" },
                        { value: "Other", label: "Other / No religion" }
                    ]
                }
            ]
        },
        {
            title: "Geographic Details",
            fields: [
                {
                    name: "nationality",
                    label: "Nationality",
                    type: "text",
                    placeholder: "e.g., Indian, NRI"
                },
                {
                    name: "domicile_state",
                    label: "Domicile State",
                    type: "text",
                    placeholder: "State of permanent residence"
                },
                {
                    name: "district",
                    label: "District",
                    type: "text",
                    placeholder: "Your district"
                },
                {
                    name: "mandal",
                    label: "Mandal / Tehsil",
                    type: "text",
                    placeholder: "Sub-district / Tehsil"
                },
                {
                    name: "pincode",
                    label: "Pincode",
                    type: "number",
                    placeholder: "6-digit postal code"
                }
            ]
        }
    ];

    const filteredGroups = fieldGroups.filter((_, index) => visibleGroups.includes(index));

    const renderInputField = (field) => {
        const commonClasses = "w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:ring-2 focus:ring-blue-500/10 focus:ring-offset-0 transition-all outline-none hover:border-blue-400 placeholder:text-gray-400";

        if (field.type === "multiselect") {
            const selectedValues = formData[field.name] || [];
            const isMaxReached = selectedValues.length >= 3;

            const hasError = validationErrors[field.name];
            return (
                <div className="relative" ref={professionDropdownRef}>
                    {/* Selected items display / trigger */}
                    <div
                        onClick={() => setProfessionDropdownOpen(!professionDropdownOpen)}
                        className={`${commonClasses} min-h-[42px] cursor-pointer flex flex-wrap gap-1.5 items-center pr-10 ${professionDropdownOpen ? 'ring-2 ring-blue-500/10 border-blue-400' : ''} ${hasError ? 'bg-red-50 border-red-300' : ''}`}
                    >
                        {selectedValues.length === 0 ? (
                            <span className="text-gray-400">Select profession(s)</span>
                        ) : (
                            selectedValues.map(value => (
                                <span
                                    key={value}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium"
                                >
                                    {value}
                                    <button
                                        type="button"
                                        onClick={(e) => removeProfession(value, e)}
                                        className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))
                        )}
                    </div>

                    {/* Dropdown arrow */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                        <ChevronDown className={`w-4 h-4 transition-transform ${professionDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>

                    {/* Hint text */}
                    <p className="text-[9px] text-gray-400 mt-1 ml-1">
                        {field.hint} • Selected: {selectedValues.length}/3
                    </p>
                    {/* Validation error for profession */}
                    {validationErrors.profession && (
                        <p className="text-xs text-red-600 mt-1">{validationErrors.profession}</p>
                    )}

                    {/* Dropdown menu */}
                    {professionDropdownOpen && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border-2 border-blue-400 rounded-lg shadow-lg max-h-64 overflow-y-auto" style={{ maxHeight: '280px' }}>
                            {field.options.map((option, index) => {
                                const isSelected = selectedValues.includes(option.value);
                                const isDisabled = !isSelected && isMaxReached;
                                const isLast = index === field.options.length - 1;

                                return (
                                    <div
                                        key={option.value}
                                        onClick={() => !isDisabled && handleProfessionChange(option.value)}
                                        className={`px-4 py-2 flex items-center gap-3 transition-colors ${!isLast ? 'border-b border-gray-100' : ''} ${isDisabled
                                            ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                            : isSelected
                                                ? 'bg-blue-50 text-blue-600 cursor-pointer'
                                                : 'hover:bg-blue-50 cursor-pointer text-gray-700'
                                            }`}
                                    >
                                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isSelected
                                            ? 'bg-blue-500 border-blue-500'
                                            : isDisabled
                                                ? 'border-gray-300'
                                                : 'border-gray-400'
                                            }`}>
                                            {isSelected && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                        <span className="text-sm">{option.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        if (field.type === "select") {
            const hasError = validationErrors[field.name];
            return (
                <div>
                    <div className="relative">
                        <select
                            name={field.name}
                            value={formData[field.name] || ""}
                            onChange={handleChange}
                            className={`${commonClasses} appearance-none pr-10 ${hasError ? 'bg-red-50 border-red-300' : ''}`}
                        >
                            {field.options.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                            <ChevronDown className="w-4 h-4" />
                        </div>
                    </div>
                    {hasError && (
                        <p className="text-xs text-red-600 mt-1">{validationErrors[field.name]}</p>
                    )}
                </div>
            );
        }

        return (
            <input
                type={field.type}
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleChange}
                placeholder={field.placeholder}
                className={commonClasses}
            />
        );
    };

    return (
        <div className="space-y-4 animate-fadeIn">
            {/* Field Groups */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredGroups.map((group, groupIndex) => (
                    <div
                        key={groupIndex}
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm shadow-gray-200/50"
                    >
                        <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/30">
                            <h4 className="font-semibold text-gray-800 text-[11px] uppercase tracking-wider">{group.title}</h4>
                        </div>

                        <div className="p-4 space-y-3">
                            {group.fields.map((field, fieldIndex) => {
                                // Check if field should be shown based on condition
                                if (field.condition && !field.condition()) {
                                    return null;
                                }
                                // Check if field is required (for step 2: gender, education, profession; for step 3: category, marital_status, religion, nationality, domicile_state, district, pincode)
                                const isRequired = (visibleGroups.includes(0) && (field.name === 'gender' || field.name === 'education')) ||
                                                  (visibleGroups.includes(1) && field.name === 'profession') ||
                                                  (visibleGroups.includes(2) && ['category', 'marital_status', 'religion', 'nationality', 'domicile_state', 'district', 'pincode'].includes(field.name));
                                
                                return (
                                    <div key={fieldIndex} className="space-y-1">
                                        <label className="text-[10px] font-bold text-gray-500 ml-1">
                                            {field.label} {isRequired && <span className="text-red-500">*</span>}
                                        </label>
                                        {renderInputField(field)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Privacy Note */}
            <div className="mt-4 flex items-start gap-3 p-3 bg-blue-50/50 rounded-xl border border-blue-100/50">
                <Shield className="w-4 h-4 text-blue-500 mt-0.5" />
                <p className="text-[10px] text-blue-800 leading-tight">
                    <span className="font-semibold">Privacy Note:</span> Encrypted and stored securely. Used only for personalization.
                </p>
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
            `}</style>
        </div>
    );
};

export default SignUpStep3;